import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaTrash } from 'react-icons/fa';
import './ThumbnailRearrange.css';

const SortableThumbnail = React.memo(({ id, image, onRemove, onThumbnailClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  // Handle confirmation before removing
  const handleConfirmRemove = () => {
    const confirmDelete = window.confirm('Are you sure you want to remove this image?');
    if (confirmDelete) {
      onRemove(id);
    }
  };

  // Separate handler for thumbnail click
  const handleThumbnailClick = (e) => {
    if (isDragging) return; // Skip if we're dragging
    
    // Only process as a click if it's a direct click (not a drag)
    if (e.detail === 1) { // Single click
      e.stopPropagation();
      console.log('Thumbnail clicked!', id);
      
      // Call the click handler
      if (onThumbnailClick) {
        onThumbnailClick(id);
      }
    }
  };

  // Base styling for each thumbnail container
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '130px',
    height: '130px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden',
    background: '#fff',
    position: 'relative', // required for absolute-positioned child
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="thumbnail-container"
      data-dragging={isDragging}
    >
      {/* Make the whole container draggable */}
      <div 
        className="drag-handle" 
        {...attributes} 
        {...listeners} 
        onClick={handleThumbnailClick} // Add click handler directly to draggable area
      >
        <img
          src={image.url}
          alt="Thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none', // Prevent image from interfering with drag
          }}
        />
      </div>

      {/* Trash button - only show if onRemove is provided */}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // prevent the drag from starting when clicking delete
            handleConfirmRemove();
          }}
          className="delete-btn"
          title="Remove image"
        >
          <FaTrash />
        </button>
      )}
    </div>
  );
});

const ThumbnailRearrange = React.memo(({ imagePreviews, onReorder, onRemove, onThumbnailClick }) => {
  // DnD sensors with delay to distinguish between click and drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Reduced from 10px   to make it easier to start dragging on touch devices
        tolerance: 5, // Adding tolerance to make touch more forgiving
        delay: 150, // Add a small delay to distinguish between tap and drag on touch devices
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reordering logic
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = imagePreviews.findIndex((img) => img.id === active.id);
    const newIndex = imagePreviews.findIndex((img) => img.id === over.id);

    if (oldIndex !== newIndex) {
      const reorderedItems = arrayMove(imagePreviews, oldIndex, newIndex);
      onReorder(reorderedItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="thumbnails-wrapper">
        <SortableContext items={imagePreviews.map((img) => img.id)}>
          <div className="thumbnails-container">
            {imagePreviews.map((image) => (
              <SortableThumbnail
                key={image.id}
                id={image.id}
                image={image}
                onRemove={onRemove}
                onThumbnailClick={onThumbnailClick}
              />
            ))}
          </div>
        </SortableContext>
      </div>
    </DndContext>
  );
}, (prevProps, nextProps) => {
  // Memo check: only re-render if these props changed
  return (
    JSON.stringify(prevProps.imagePreviews) === JSON.stringify(nextProps.imagePreviews) &&
    prevProps.onReorder === nextProps.onReorder &&
    prevProps.onRemove === nextProps.onRemove &&
    prevProps.onThumbnailClick === nextProps.onThumbnailClick
  );
});

export default ThumbnailRearrange;
